/**
 * @fileoverview Document Metadata Panel
 *
 * Purpose: Displays classification results and extracted structured metadata.
 * Handles both old (key-value) and new (standardised sections) metadata shapes.
 * Allows practitioners to correct category/documentType and mark as verified.
 *
 * @component
 * @example
 * <DocumentMetadataPanel documentId={documentId} isViewer={false} />
 */

import { useState, type ReactNode } from "react";
import { usePersistedDisclosure } from "@/utils/use-persisted-disclosure";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { DocumentCategory } from "@cvx/jobs/gatherInformation/documents/schema";
import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import { Switch } from "@/ui/primitives/switch";
import { Label } from "@/ui/primitives/label";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { Separator } from "@/ui/primitives/separator";
import {
  CheckCircle2,
  Save,
  Loader2,
  Brain,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  AlertCircle,
  Info,
  Calendar,
  DollarSign,
  Users,
  Building2,
  Tag,
  FileText,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { CATEGORY_LABELS } from "../constants";
import { CategoryExtractionPanel } from "./extractions/CategoryExtractionPanel";
import { coerceStructuredMetadata } from "./structured-metadata-normalize";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";

interface DocumentMetadataPanelProps {
  documentId: Id<"documents">;
  isViewer: boolean;
}

/** Collapsible section wrapper used for each metadata section. */
function CollapsibleSection({
  icon,
  title,
  count,
  defaultOpen = true,
  children,
}: {
  icon: ReactNode;
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  // Titles are stable + unique within this panel, so key off them directly so
  // the user's show/hide choice per section persists across sessions.
  const [open, setOpen] = usePersistedDisclosure(
    `document-metadata:${title}`,
    defaultOpen,
  );

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="-mx-1 flex w-full items-center gap-1.5 rounded px-1 py-0.5 transition-colors hover:bg-muted/30"
      >
        {open ? (
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
        )}
        <span className="flex items-center gap-1.5">
          {icon}
          <span className="text-sm font-medium">{title}</span>
        </span>
        {count !== undefined && (
          <Badge variant="secondary" size="sm" className="ml-auto">
            {count}
          </Badge>
        )}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

/**
 * Displays and allows editing of document classification and metadata.
 */
export function DocumentMetadataPanel({
  documentId,
  isViewer,
}: DocumentMetadataPanelProps) {
  const { data: detail } = useQuery(
    convexQuery(
      api.jobs.gatherInformation.documents.queries.getDocumentDetail,
      { documentId },
    ),
  );

  const { mutateAsync: updateMetadata, isPending: isSaving } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.gatherInformation.documents.mutations.updateDocumentMetadata,
    ),
  });

  const [editCategory, setEditCategory] = useState<string | null>(null);
  const [editVerified, setEditVerified] = useState<boolean | null>(null);
  const [showReasoning, setShowReasoning] = useState(false);

  if (!detail) return null;

  const currentCategory = editCategory ?? detail.category;
  const currentVerified = editVerified ?? detail.practitionerVerified ?? false;
  const hasEdits = editCategory !== null || editVerified !== null;

  // Use classificationConfidence if available, fall back to metadataConfidence
  const confidence =
    ((detail as Record<string, unknown>).classificationConfidence as
      number | undefined) ?? detail.metadataConfidence;
  const reasoning = (detail as Record<string, unknown>)
    .classificationReasoning as string | undefined;

  const handleSave = async () => {
    try {
      await updateMetadata({
        documentId,
        category: editCategory ? (editCategory as DocumentCategory) : undefined,
        documentType: undefined,
        verified: editVerified ?? undefined,
      });
      setEditCategory(null);
      setEditVerified(null);
      toast.success("Metadata updated");
    } catch {
      toast.error("Failed to update metadata");
    }
  };

  const meta = detail.structuredMetadata as unknown;
  const newMeta = coerceStructuredMetadata(meta);
  const isNew = newMeta !== null;

  return (
    <div className="flex flex-col gap-4">
      {/* AI Classification header */}
      <div>
        <div className="mb-3 flex items-center gap-2">
          <Brain className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium">AI Classification</h4>
          {confidence !== undefined && (
            <Badge
              variant={
                confidence > 0.8
                  ? "default"
                  : confidence > 0.5
                    ? "secondary"
                    : "destructive"
              }
              size="sm"
            >
              {Math.round(confidence * 100)}% confidence
            </Badge>
          )}
        </div>

        {/* Category dropdown */}
        {!isViewer && (
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">Category</Label>
            <Select
              value={currentCategory}
              onValueChange={(v) => setEditCategory(v)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_LABELS)
                  .filter(([key]) => key !== "all")
                  .map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {isViewer && (
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">Category</Label>
            <span className="text-sm">
              {CATEGORY_LABELS[currentCategory] ?? currentCategory}
            </span>
          </div>
        )}

        {/* Collapsible reasoning */}
        {reasoning && (
          <button
            onClick={() => setShowReasoning(!showReasoning)}
            className="mt-3 flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            {showReasoning ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            Classification reasoning
          </button>
        )}
        {showReasoning && reasoning && (
          <p className="mt-1 rounded bg-muted/50 p-2 text-xs text-muted-foreground">
            {reasoning}
          </p>
        )}

        {/* Verified switch + Save */}
        {!isViewer && (
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch
                id="verified"
                checked={currentVerified}
                onCheckedChange={(checked) => setEditVerified(checked)}
              />
              <Label htmlFor="verified" className="text-xs">
                Verified by practitioner
              </Label>
              {currentVerified && (
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              )}
            </div>

            {hasEdits && (
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isSaving}
                className="gap-1 text-xs"
              >
                {isSaving ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Save className="h-3 w-3" />
                )}
                Save Changes
              </Button>
            )}
          </div>
        )}
      </div>

      <Separator />

      {/* Category-specific extractions (new architecture) */}
      {detail.extractionCategories &&
        detail.extractionCategories.length > 0 && (
          <CategoryExtractionPanel documentId={documentId} />
        )}

      {/* Legacy: unified metadata shape rendering */}
      {isNew && newMeta ? (
        <>
          {/* 2. Summary */}
          {newMeta.summary && (
            <CollapsibleSection
              icon={<FileText className="h-3.5 w-3.5 text-muted-foreground" />}
              title="Summary"
            >
              <p className="text-xs leading-relaxed text-muted-foreground">
                {newMeta.summary}
              </p>
            </CollapsibleSection>
          )}

          {/* 3. Flags */}
          {newMeta.flags.length > 0 && (
            <>
              <Separator />
              <CollapsibleSection
                icon={
                  <ShieldAlert className="h-3.5 w-3.5 text-muted-foreground" />
                }
                title="Flags"
                count={newMeta.flags.length}
              >
                <div className="space-y-2">
                  {newMeta.flags.map((flag, idx) => {
                    const FlagIcon =
                      flag.severity === "critical"
                        ? AlertTriangle
                        : flag.severity === "warning"
                          ? AlertCircle
                          : Info;
                    const colourClass =
                      flag.severity === "critical"
                        ? "border-red-200 bg-red-50 text-red-900"
                        : flag.severity === "warning"
                          ? "border-amber-200 bg-amber-50 text-amber-900"
                          : "border-blue-200 bg-blue-50 text-blue-900";

                    return (
                      <div
                        key={idx}
                        className={`flex gap-2 rounded-lg border p-2 ${colourClass}`}
                      >
                        <FlagIcon className="mt-0.5 h-4 w-4 shrink-0" />
                        <div>
                          <div className="text-xs font-medium">{flag.flag}</div>
                          <div className="text-[11px] opacity-80">
                            {flag.detail}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CollapsibleSection>
            </>
          )}

          {/* 4. Key Amounts */}
          {newMeta.keyAmounts.length > 0 && (
            <>
              <Separator />
              <CollapsibleSection
                icon={
                  <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                }
                title="Key Amounts"
                count={newMeta.keyAmounts.length}
              >
                <div className="overflow-hidden rounded-lg border">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="px-3 py-2 text-left text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                          Description
                        </th>
                        <th className="px-3 py-2 text-right text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...newMeta.keyAmounts]
                        .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
                        .map((item, idx) => (
                          <tr
                            key={idx}
                            className="border-t border-border/50 transition-colors hover:bg-muted/30"
                          >
                            <td className="px-3 py-2">
                              <span className="text-xs font-medium">
                                {item.label}
                              </span>
                              {item.context && (
                                <span className="mt-0.5 block text-[10px] text-muted-foreground">
                                  {item.context}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <span
                                className={`font-mono text-xs font-medium tabular-nums ${
                                  item.amount < 0
                                    ? "text-red-600"
                                    : "text-foreground"
                                }`}
                              >
                                $
                                {Math.abs(item.amount).toLocaleString(
                                  undefined,
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  },
                                )}
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </CollapsibleSection>
            </>
          )}

          {/* 5. Key Dates */}
          {newMeta.keyDates.length > 0 && (
            <>
              <Separator />
              <CollapsibleSection
                icon={
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                }
                title="Key Dates"
                count={newMeta.keyDates.length}
              >
                <div className="overflow-hidden rounded-lg border">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="px-3 py-2 text-left text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                          Event
                        </th>
                        <th className="w-32 px-3 py-2 text-right text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                          Date
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...newMeta.keyDates]
                        .sort((a, b) => a.date.localeCompare(b.date))
                        .map((item, idx) => (
                          <tr
                            key={idx}
                            className="border-t border-border/50 transition-colors hover:bg-muted/30"
                          >
                            <td className="px-3 py-1.5 text-xs">
                              {item.label}
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums text-muted-foreground">
                              {item.date}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </CollapsibleSection>
            </>
          )}

          {/* 6. Key Parties */}
          {newMeta.keyParties.length > 0 && (
            <>
              <Separator />
              <CollapsibleSection
                icon={<Users className="h-3.5 w-3.5 text-muted-foreground" />}
                title="Key Parties"
                count={newMeta.keyParties.length}
              >
                <div className="space-y-1">
                  {newMeta.keyParties.map((party, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      <span>{party.name}</span>
                      <Badge
                        variant="outline"
                        className="px-1.5 py-0 text-[10px]"
                      >
                        {party.role}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            </>
          )}

          {/* 7. Entities */}
          {(newMeta.entities.primaryEntity ||
            (newMeta.entities.relatedEntities &&
              newMeta.entities.relatedEntities.length > 0)) && (
            <>
              <Separator />
              <CollapsibleSection
                icon={
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                }
                title="Entities"
              >
                {newMeta.entities.primaryEntity && (
                  <div className="mb-1 text-xs">
                    <span className="text-muted-foreground">Primary: </span>
                    <span className="font-medium">
                      {newMeta.entities.primaryEntity}
                    </span>
                  </div>
                )}
                {newMeta.entities.relatedEntities &&
                  newMeta.entities.relatedEntities.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {newMeta.entities.relatedEntities.map((entity, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {entity}
                        </Badge>
                      ))}
                    </div>
                  )}
              </CollapsibleSection>
            </>
          )}

          {/* 8. Additional Metadata */}
          {newMeta.metadata && Object.keys(newMeta.metadata).length > 0 && (
            <>
              <Separator />
              <CollapsibleSection
                icon={<Tag className="h-3.5 w-3.5 text-muted-foreground" />}
                title="Additional Metadata"
                count={Object.keys(newMeta.metadata).length}
                defaultOpen={false}
              >
                <div className="overflow-hidden rounded-lg border">
                  <table className="w-full">
                    <tbody>
                      {Object.entries(newMeta.metadata).map(
                        ([key, value], idx) => {
                          if (value === null || value === undefined)
                            return null;
                          const formattedKey = key
                            .replace(/([A-Z])/g, " $1")
                            .replace(/[_-]/g, " ")
                            .trim()
                            .replace(/^\w/, (c) => c.toUpperCase());
                          return (
                            <tr
                              key={key}
                              className={`${idx > 0 ? "border-t border-border/50" : ""} transition-colors hover:bg-muted/30`}
                            >
                              <td className="w-1/3 px-3 py-2 align-top text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                {formattedKey}
                              </td>
                              <td className="px-3 py-2 text-xs text-foreground">
                                {typeof value === "boolean" ? (
                                  <Badge
                                    variant={value ? "default" : "secondary"}
                                    className="text-[10px]"
                                  >
                                    {value ? "Yes" : "No"}
                                  </Badge>
                                ) : (
                                  String(value)
                                )}
                              </td>
                            </tr>
                          );
                        },
                      )}
                    </tbody>
                  </table>
                </div>
              </CollapsibleSection>
            </>
          )}
        </>
      ) : (
        /* Legacy metadata shape — old key-value display */
        detail.structuredMetadata && (
          <div>
            <h4 className="mb-2 text-sm font-medium">Extracted Metadata</h4>
            <div className="space-y-2">
              {Object.entries(
                detail.structuredMetadata as Record<string, unknown>,
              ).map(([key, value]) => {
                if (value === null || value === undefined) return null;
                return (
                  <div key={key} className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      {key.replace(/([A-Z])/g, " $1").trim()}
                    </span>
                    <span className="text-xs">{String(value)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )
      )}

      {/* Legacy: Date Range */}
      {detail.dateRangeCovered && (
        <>
          <Separator />
          <div>
            <h4 className="mb-1 text-sm font-medium">Date Range</h4>
            <span className="text-xs text-muted-foreground">
              {detail.dateRangeCovered.start ?? "?"} —{" "}
              {detail.dateRangeCovered.end ?? "?"}
            </span>
          </div>
        </>
      )}

      {/* Legacy: Parties Mentioned */}
      {detail.partiesMentioned && detail.partiesMentioned.length > 0 && (
        <>
          <Separator />
          <div>
            <h4 className="mb-1 text-sm font-medium">Parties Mentioned</h4>
            <div className="flex flex-wrap gap-1">
              {detail.partiesMentioned.map((party) => (
                <Badge key={party} variant="outline" className="text-xs">
                  {party}
                </Badge>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Legacy: Key Amounts */}
      {!isNew && detail.keyAmounts && detail.keyAmounts.length > 0 && (
        <>
          <Separator />
          <div>
            <h4 className="mb-1 text-sm font-medium">Key Amounts</h4>
            <div className="space-y-1">
              {detail.keyAmounts.map((item, idx) => (
                <div key={idx} className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{item.label}</span>
                  <span className="font-mono">
                    ${item.amount.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <AiDisclaimer className="pt-1" />
    </div>
  );
}
